import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Users, Building2, Cloud, ShieldCheck, KeyRound, Clock, Package, Sparkles,
  Plus, Cpu, Bell, Brain, User as UserIcon, Info, Ticket, Terminal,
  LayoutDashboard, ScrollText,
} from 'lucide-react';
import { SidebarShell } from '@cloudgenie/ui';
import type { SidebarNavItem, SidebarNavSection } from '@cloudgenie/ui';
import { useAuth, hasCap } from '../AuthContext';

type GatedItem = SidebarNavItem & { requireAdmin?: boolean; requireSuperAdmin?: boolean; restrictedBlocked?: boolean };

const NEW_SECTIONS: GatedItem[] = [
  { path: '/admin', icon: LayoutDashboard, label: 'Overview' },
  { path: '/admin/tenants', icon: Building2, label: 'Tenants' },
  { path: '/admin/users', icon: Users, label: 'Users' },
  { path: '/admin/api-keys', icon: KeyRound, label: 'API Keys' },
  { path: '/cli-authorize', icon: Terminal, label: 'CLI authorize' },
];

const LEGACY_SECTIONS: GatedItem[] = [
  { path: '/admin/users-groups', icon: Users, label: 'Users & Groups', restrictedBlocked: true },
  { path: '/admin/organization', icon: Building2, label: 'Organization' },
  { path: '/admin/cloud-config', icon: Cloud, label: 'Cloud Config Hub' },
  { path: '/admin/policies', icon: ShieldCheck, label: 'CGPL Policies' },
  { path: '/admin/secret-brokers', icon: KeyRound, label: 'Secret Brokers', badge: 'Soon', restrictedBlocked: true },
  { path: '/admin/scheduler', icon: Clock, label: 'Scheduler' },
  { path: '/admin/modules', icon: Package, label: 'Modules', requireSuperAdmin: true },
  { path: '/admin/trials', icon: Sparkles, label: 'Trial Funnel', requireAdmin: true },
  { path: '/admin/add-service', icon: Plus, label: 'Add IaC Service', requireAdmin: true },
  { path: '/admin/agents', icon: Cpu, label: 'Agents' },
  { path: '/admin/platform-health', icon: Cloud, label: 'Platform Health' },
  { path: '/admin/notifications', icon: Bell, label: 'Notifications' },
  { path: '/admin/settings', icon: Brain, label: 'AI & Settings', restrictedBlocked: true },
  { path: '/admin/test-users', icon: KeyRound, label: 'Test Users', requireSuperAdmin: true },
  { path: '/admin/audit-log', icon: ScrollText, label: 'Audit Log', requireSuperAdmin: true },
  { path: '/admin/profile', icon: UserIcon, label: 'My Profile' },
  { path: '/admin/about', icon: Info, label: 'About' },
  { path: '/billing', icon: Ticket, label: 'Billing & Plans', restrictedBlocked: true },
];

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();

  const isAdmin = user && hasCap(user, 'admin.ops');
  const isSuperAdmin = user?.role === 'super_admin';
  const isRestricted = Boolean(user?.restricted_admin) && !isSuperAdmin;

  const gate = (items: GatedItem[]) => items.filter((i) => {
    if (i.requireSuperAdmin && !isSuperAdmin) return false;
    if (i.requireAdmin && !isAdmin && !isSuperAdmin) return false;
    if (i.restrictedBlocked && isRestricted) return false;
    return true;
  });

  const sections: SidebarNavSection[] = [
    { title: 'Admin console', accent: 'blue', items: gate(NEW_SECTIONS) },
    { title: 'Platform admin', accent: 'amber', items: gate(LEGACY_SECTIONS) },
  ];

  return (
    <SidebarShell
      product="id"
      title="CloudGenie ID"
      subtitle="Identity & Access Console"
      trust={[
        { label: 'Operational', kind: 'live' },
        { label: 'SSO • RBAC • AUDIT', kind: 'plain' },
      ]}
      sections={sections}
      user={user ?? undefined}
      testidPrefix="admin"
      rootKey="root"
      rootPath="/admin"
      currentPath={loc.pathname}
      LinkComponent={Link}
      onNavigate={(p) => navigate(p)}
      onSignOut={logout}
    >
      {children}
    </SidebarShell>
  );
}

export default AdminLayout;
